import { apiFailure, featureListSuccess, fetchOrgList, initAdminInvite, initFeatureListFetch, initLicenceListFetch, initOrgCSVDataFetch, initOrgCreate, initOrgDetailFetch, initOrgUpdate, initWhiteListFetch, initWhiteListItemAdd, initWhiteListItemRemove, licenceListSuccess, orgCSVDataFetchSuccess, orgDetailSuccess, orgListSuccess, resetApiState } from './actions';
import { doGet, doPatch, doPost } from '../../service';

import APIConfig from '../../service/api-config';
import BaseResponse from '../../types/base-response';
import Feature from '../../types/feature';
import FeatureListResponse from '../../types/feature-list-response';
import FetchWhitelistResponse from '../../types/fetch-whitelist-response';
import InviteAdminResponse from '../../types/invite-admin-response';
import LicenceListResponse from '../../types/licence-list-response';
import OrgCSVDataResponse from '../../types/org-csv-data-response';
import Organization from '../../types/organization';
import OrganizationListResponse from '../../types/org-list-response';
import OrganizationResponse from '../../types/organization-response';
import React from 'react';
import { Store } from '../../store/store';
import Util from '../../utils/util';
import WhiteListItem from '../../types/whitelist-Item';
import { t } from 'i18next';
import { useCallback } from 'react';
import AddWhitelistItemResponse from '../../types/add-whitelist-item-response';

/**
 * Custom Hook for Organization API Calls
 *
 * This hook provides functions for fetching and managing organization data using API calls.
 * It utilizes the `organizationReducer` and initial state (`orgInitialState`) for state management.
 *
 * @returns {Object} - Returns an object containing the exposed functions for utilizing organization API functionalities.
 */
export function useOrganizationApi() {

	const { state, dispatch } = React.useContext(Store);

	/**
	 * Fetches the organization list from the API endpoint defined in `APIConfig.organization`.
	 * Dispatches actions to manage the fetch process and handle success/error scenarios.
	 */
	const fetchOrganizationList = useCallback(async () => {
		dispatch(fetchOrgList());
		try {
			const response: OrganizationListResponse = await doGet(APIConfig.organization);
			const orgList: Array<Organization> = [];
			if (!Util.isArrayEmpty(response.data)) {
				response.data?.forEach((item) => {
					const organization: Organization = {
						details: item,
						admins: [],
						services: []
					};
					orgList.push(organization);
				});
			}
			dispatch(orgListSuccess(orgList));

			return response.data;
		} catch (error: any) { /* eslint-disable-line */
			dispatchFailureAction(error);
		}
	}, []);

	/**
	 * Fetches the licence list from the API endpoint defined in `APIConfig.licence`.
	 * Dispatches actions to manage the fetch process and handle success/error scenarios.
	 */
	const fetchLicenceList = useCallback(async () => {
		dispatch(initLicenceListFetch());
		try {
			const response: LicenceListResponse = await doGet(APIConfig.licence);
			if (!Util.isArrayEmpty(response.data)) {
				response.data.sort((a, b) => a.maxSize - b.maxSize).forEach(item => {
					item.value = t('upToMaxSize', { maxSize: item.maxSize });
				});
			}
			dispatch(licenceListSuccess(response.data || []));
		} catch (error: any) { /* eslint-disable-line */
			dispatchFailureAction(error);
		}
	}, []);

	/**
	 * Fetches the feature list from the API endpoint defined in `APIConfig.features`.
	 * Dispatches actions to manage the fetch process and handle success/error scenarios.
	 */
	const fetchFeatureList = useCallback(async () => {
		dispatch(initFeatureListFetch());
		try {
			const response: FeatureListResponse = await doGet(APIConfig.features);
			const featureList = response.data || [];

			// Iterate through each feature
			featureList.forEach(feature => {
				// Check if any child item has value set to true
				if (feature.data && feature.data.some(child => child.value)) {
					// Set the parent value to true if any child has value true
					feature.value = true;
					feature.disabled = true;
				}
			});
			dispatch(featureListSuccess(featureList));
		} catch (error: any) { /* eslint-disable-line */
			dispatchFailureAction(error);
		}
	}, []);

	/**
	 * Fetches the details of a specific organization by its ID.
	 * Dispatches actions to manage the fetch process and handle success/error scenarios.
	 *
	 * @param {string} orgId - The ID of the organization to fetch details for.
	 *
	 * @returns {Promise<Organization>} - The fetched organization data.
	 */
	const fetchOrganizationDetail = useCallback(async (orgId: string) => {
		dispatch(initOrgDetailFetch());
		try {
			const response: OrganizationResponse = await doGet(`${APIConfig.organization}/${orgId}`);
			response.data.isFetched = true;
			dispatch(orgDetailSuccess(response.data));

			return response.data;
		} catch (error: any) { /* eslint-disable-line */
			dispatchFailureAction(error);
		}
	}, [state.orgState.orgList]);

	/**
	 * Creates a new organization by sending a POST request to the API.
	 * Dispatches actions to initiate the creation process and manage success/error scenarios.
	 *
	 * @param {Organization} organization - The organization object containing the details to be created.
	 *
	 * @returns {Promise<Organization>} - The newly created organization data.
	 */
	const createOrganization = useCallback(async (organization: Organization) => {
		dispatch(initOrgCreate());
		try {
			const response: OrganizationResponse = await doPost(APIConfig.organization, organization);
			response.data.isFetched = true;
			const orgList = [...state.orgState.orgList, response.data];
			dispatch(orgListSuccess(orgList));
			return response.data;
		} catch (error: any) { /* eslint-disable-line */
			dispatchFailureAction(error);
		}
	}, [state.orgState.orgList]);

	/**
	 * Updates an existing organization by sending a PATCH request to the API.
	 * Dispatches actions to initiate the update process and manage success/error scenarios.
	 *
	 * @param {Organization} organization - The organization object containing the updated details.
	 *
	 * @returns {Promise<Organization>} - The updated organization data.
	 */
	const updateOrganization = useCallback(async (org: Organization) => {
		dispatch(initOrgUpdate());
		try {
			const request = {
				...org,
				details: {
					...org.details,
					id: undefined
				}
			};
			const response: OrganizationResponse = await doPatch(`${APIConfig.organization}/${org.details.id}`, request);
			const updatedOrganization = {
				...response.data,
				isFetched: true,
			};
			const updatedOrgList = state.orgState.orgList.map(existingOrg =>
				existingOrg.details.id === org.details.id
					? { ...existingOrg, ...updatedOrganization }
					: existingOrg
			);
			dispatch(orgListSuccess(updatedOrgList));

			return response.data;
		} catch (error: any) {/* eslint-disable-line */
			dispatchFailureAction(error);
		}
	}, [state.orgState.orgList]);

	/**
	 * Invites an administrator to join the organization by sending a patch request.
	 *
	 * @param {string} invite - The invite URL or identifier for the admin to be invited.
	 * @returns {Promise<any>} - A promise that resolves with the response data if the request is successful.
	 *                            If the request fails, it does not resolve; the error is handled internally.
	 */
	const inviteOrganizationAdmin = useCallback(async (invite: string) => {
		dispatch(initAdminInvite());
		try {
			const response: InviteAdminResponse = await doPatch(`${invite}`);
			dispatch(resetApiState())
			return response.data;
		} catch (error: any) { /* eslint-disable-line */
			dispatchFailureAction(error);
		}
	}, []);

	/**
	 * Fetches the whitelist for a specific organization.
	 *
	 * This function dispatches an action to initiate the whitelist fetch, makes an API call to retrieve the whitelist data,
	 * updates the organization list in the state with the fetched whitelist, and dispatches a success action.
	 * It also handles potential errors during the API call and dispatches a failure action if necessary.
	 *
	 * @param {Organization} organisation - The organization for which to fetch the whitelist.
	 * @returns {Promise<void>} - A Promise that resolves when the whitelist is fetched and the state is updated.
	 */
	const fetchOrganizationWhitelist = useCallback(async (organisation: Organization) => {
		dispatch(initWhiteListFetch());
		try {
			const url = APIConfig.fetchWhiteList
				.replace('{page}', '0')
				.replace('{subdomain}', organisation.details.domain);
			const response: FetchWhitelistResponse = await doGet(url);
			const updatedOrgList = state.orgState.orgList.map(org =>
				org.details.id === organisation.details.id
					? { ...org, whitelist: response.data }
					: org
			);
			dispatch(orgListSuccess(updatedOrgList));
		} catch (error: any) { /* eslint-disable-line */
			dispatchFailureAction(error);
		}
	}, []);

	/**
	 * Adds a phone number to the whitelist for a specific organization.
	 *
	 * This function dispatches an action to initiate the whitelist item addition, makes an API call to add the phone number,
	 * updates the organization list in the state by adding the new entry to the organization's whitelist,
	 * and dispatches a success action. It also handles potential errors during the API call and dispatches a failure action.
	 *
	 * @param {Organization} organisation - The organization to add the phone number to.
	 * @param {string} phoneNumber - The phone number to add to the whitelist.
	 * @returns {Promise<void>} - A Promise that resolves when the phone number is added and the state is updated.
	 */
	const addWhitelistNumber = useCallback(
		async (organisation: Organization, phoneNumber: string) => {
			dispatch(initWhiteListItemAdd());
			try {
				const url = APIConfig.addWhiteListItem
					.replace('{subdomain}', organisation.details.domain)
					.concat(phoneNumber)
				const response: AddWhitelistItemResponse = await doPost(url);
				const updatedOrgList = state.orgState.orgList.map(org =>
					org.details.id === organisation.details.id
						? { ...org, whitelist: [...(org.whitelist || []), response.data] }
						: org
				);
				dispatch(orgListSuccess(updatedOrgList));
			} catch (error: any) {/* eslint-disable-line */
				dispatchFailureAction(error);
			}
		},
		[state.orgState.orgList]
	);

	/**
	 * Removes a phone number from the whitelist for a specific organization.
	 *
	 * This function dispatches an action to initiate the whitelist item removal, makes an API call to remove the phone number,
	 * updates the organization list in the state by filtering out the removed entry from the organization's whitelist,
	 * and dispatches a success action. It also handles potential errors during the API call.
	 *
	 * @param {Organization} organisation - The organization to remove the phone number from.
	 * @param {WhiteListItem} item - The whitelist item (containing the phone number) to remove.
	 * @returns {Promise<void>} - A Promise that resolves when the phone number is removed and the state is updated.
	 */
	const removeWhitelistNumber = useCallback(
		async (organisation: Organization, item: WhiteListItem) => {
			dispatch(initWhiteListItemRemove());
			try {
				const url = APIConfig.removeWhiteListItem
					.replace('{subdomain}', organisation.details.domain)
					.concat(item.phoneNumber)
				const response: BaseResponse = await doPost(url);
				if (response && response.ok) {
					const updatedOrgList = state.orgState.orgList.map(org =>
						org.details.id === organisation.details.id
							? { ...org, whitelist: org.whitelist?.filter((whitelistItem: WhiteListItem) => whitelistItem.phoneNumber !== item.phoneNumber) }
							: org
					);
					dispatch(orgListSuccess(updatedOrgList));
				}

			} catch (error: any) {/* eslint-disable-line */
				dispatchFailureAction(error);
			}
		},
		[state.orgState.orgList]
	);

	/**
	 * Checks if a given feature is enabled based on a list of enabled features.
	 * It iterates through the list of enabled features and checks if any of them match
	 * the IDs of the child features of the given feature.
	 * @param {Array<string>} featureList - A list of enabled feature IDs.
	 * @param {Feature} feature - The feature object to check.
	 * @returns {boolean} True if at least one child feature of the given feature is enabled, false otherwise.
	 */
	const isFeatureEnabled = (featureList: Array<string>, feature: Feature) => {
		let isEnabled = false;
		const childFeatureList = feature.data?.map(item => item.id);
		if (!Util.isArrayEmpty(featureList) && !Util.isArrayEmpty(childFeatureList)) {
			for (const featureToCheck of featureList) {
				// eslint-disable-next-line
				if (childFeatureList!.includes(featureToCheck)) {
					isEnabled = true;
					break;
				}
			}
		}


		return isEnabled;
	}

	/**
	 * Asynchronously fetches all organization data for CSV export.
	 * It makes an API call to retrieve the organization data and then processes
	 * the response to format it into a two-dimensional array suitable for CSV.
	 * @returns {Promise<Array<Array<string>> | undefined>} A promise that resolves to a 2D array of organization data for CSV,
	 * or undefined if there is an error during the API call.
	 */
	const fetchAllOrgCSVData = useCallback(async () => {
		dispatch(initOrgCSVDataFetch());
		try {
			const response: OrgCSVDataResponse = await doGet(APIConfig.allOrgCSVData);
			const csvDataList: Array<Array<string>> = [];
			if (!Util.isArrayEmpty(response.data)) {
				response.data.map(item => {
					// Please follow the order of insertion. If you change the order change orgCSVHeaderData() function in Constants.ts.
					const orgData: Array<string> = [];
					orgData.push(item.organization.name);
					orgData.push(item.organization.status);
					orgData.push(item.organization.epicorCustomerId ?? '-');
					orgData.push(item.organization.epicorSalesOrder ?? '-');
					orgData.push((item.organization.licence?.maxSize ?? 0).toString());
					orgData.push(Util.formatUTCtoLocal(item.organization.expiryAt));
					orgData.push(item.organization.supportEmail ?? '-');
					orgData.push(item.organization.description ?? '-');
					orgData.push(item.admins.map(admin => admin.loginId).join(','));
					const featureList = item.services.map(item => item.id);
					state.orgState.featureList.sort((a, b) =>
						a.label.toLowerCase().localeCompare(b.label.toLowerCase())
					).map(item => {
						const isEnabled = isFeatureEnabled(featureList, item);
						orgData.push(isEnabled + '');
					});
					csvDataList.push(orgData);
				});
			}
			dispatch(orgCSVDataFetchSuccess(csvDataList));

			return csvDataList;
		} catch (error: any) {/* eslint-disable-line */
			dispatchFailureAction(error);
		}
	}, [state.orgState.featureList]);

	/**
	 * Dispatches an `apiFailure` action with an error message for failure scenarios.
	 *
	 * @param {Error} error - Optional error object encountered during api processes.
	 */
	const dispatchFailureAction = (error?: any) => { /* eslint-disable-line */
		const message: string = (error && error.message) ? error.message : t('defaultErrorMsg');
		dispatch(apiFailure(message));
	}

	/**
	 * Resets the error state in the Redux store.
	 */
	const resetError = () => {
		if (state.orgState.apiStatus?.error) {
			dispatch(resetApiState());
		}
	}

	return {
		fetchOrganizationList,
		fetchLicenceList,
		fetchFeatureList,
		fetchOrganizationDetail,
		createOrganization,
		updateOrganization,
		inviteOrganizationAdmin,
		fetchOrganizationWhitelist,
		addWhitelistNumber,
		removeWhitelistNumber,
		fetchAllOrgCSVData,
		resetError,
		state
	};
}
